import { createJSONStorage, type StateStorage } from 'zustand/middleware'
import { usesHostSession } from '@/shared/bridge/env'

type StorageKind = 'local' | 'session'

const memoryBuckets: Record<StorageKind, Map<string, string>> = {
  local: new Map(),
  session: new Map(),
}

function canUseStorage(storage: Storage) {
  const key = '__qsc_storage_probe__'
  try {
    storage.setItem(key, '1')
    storage.removeItem(key)
    return true
  } catch {
    return false
  }
}

function nativeStorage(kind: StorageKind): Storage | null {
  if (typeof window === 'undefined') return null
  const storage = kind === 'session' ? window.sessionStorage : window.localStorage
  return canUseStorage(storage) ? storage : null
}

export function preferredStorageKind(): StorageKind {
  return usesHostSession() ? 'session' : 'local'
}

export function getStorage(kind: StorageKind = preferredStorageKind()): StateStorage {
  const native = nativeStorage(kind)
  if (native) return native

  const bucket = memoryBuckets[kind]
  return {
    getItem: (name) => bucket.get(name) ?? null,
    setItem: (name, value) => {
      bucket.set(name, value)
    },
    removeItem: (name) => {
      bucket.delete(name)
    },
  }
}

export const appStorage = {
  getItem: (key: string) => getStorage().getItem(key),
  setItem: (key: string, value: string) => getStorage().setItem(key, value),
  removeItem: (key: string) => getStorage().removeItem(key),
}

export const persistentStorage = createJSONStorage(() => getStorage('local'))
export const sessionAwareStorage = createJSONStorage(() => getStorage())

export function storageGet<T>(key: string): T | null {
  try {
    const raw = getStorage().getItem(key) as string | null
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    return null
  }
}

export function storageSet(key: string, value: unknown) {
  try {
    appStorage.setItem(key, JSON.stringify(value))
  } catch {
    // storage may be blocked in some embedded browsers; callers should continue.
  }
}

export function storageRemove(key: string) {
  try {
    appStorage.removeItem(key)
  } catch {
    // no-op
  }
}

export const STORAGE_KEYS = {
  AUTH: 'qsc-auth',
  ENV: 'qsc-env',
  LAYOUT: 'qsc-layout',
  SETTINGS: 'qsc-settings',
  SAVED_ACCOUNTS: 'qsc-saved-accounts',
} as const
